"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeetingNotesFollowupConfig } from "@west-tennessee-consulting/notifications/reminders";
import type { UserPreferences } from "./types";

/**
 * App-wide shared cache for the current user's preferences
 * (`app_users.user_prefs`, read/written via `GET|PATCH /api/user/preferences`).
 *
 * WHY THIS EXISTS: several features read the same preferences (saved-view tab
 * bar, per-table column/page-size prefs, product tours, Slack reminder channel).
 * Each used to `fetch("/api/user/preferences")` on its own mount, so navigating
 * between pages — or even mounting two table hooks on one page — refetched the
 * same blob repeatedly. Routing every consumer through ONE TanStack Query key
 * means the app fetches the user's preferences at most once, caches them, and
 * shares that cache across every page and component. The global query defaults
 * (`refetchOnMount:false`, `staleTime` below) ensure navigating to another page
 * does NOT refetch.
 *
 * Writes go through `useUpdateUserPreferences()`, which optimistically patches
 * the shared cache (so every reader updates instantly) and then reconciles with
 * the authoritative full preferences the PATCH returns.
 *
 * This is a `"use client"` hook module — do NOT re-export it from
 * `@/lib/user-preferences` (that barrel must stay client/server-safe). Import it
 * directly: `@/lib/user-preferences/use-user-preferences`.
 */

export const USER_PREFERENCES_QUERY_KEY = ["user-preferences"] as const;

/** One entity-scoped id list for a saved-view tab patch. */
interface EntityViewIds {
  entityName: string;
  viewIds: string[];
}

/**
 * The set of patch shapes accepted by `PATCH /api/user/preferences`. Mirrors the
 * route's `patchBodySchema`. Exactly one concern is set per call in practice.
 */
export interface UserPreferencesPatchBody {
  slackTaskReminderChannelId?: string | null;
  markTourSeen?: string;
  unmarkTourSeen?: string;
  setSavedViewHidden?: EntityViewIds;
  setSavedViewOrder?: EntityViewIds;
  setTableColumnWidths?: { tableKey: string; widths: Record<string, number> };
  setTableColumnVisibility?: {
    tableKey: string;
    visibility: Record<string, boolean>;
  };
  setTableColumnOrder?: { tableKey: string; order: string[] };
  setTablePageSize?: { tableKey: string; pageSize: number };
  /** Which CRM inbox interface to show; null resets to the default. */
  crmInboxLayout?: string | null;
  /** Mail accounts switched on in the unified inbox; [] or null = all accounts. */
  crmInboxSelectedAccountIds?: string[] | null;
  /** "Log your meeting notes" reminder settings; null turns it off. */
  setMeetingNotesFollowup?: MeetingNotesFollowupConfig | null;
}

async function fetchUserPreferences(): Promise<UserPreferences> {
  const res = await fetch("/api/user/preferences", {
    credentials: "same-origin",
  });
  if (!res.ok) return {}; // unauthenticated / error → no preferences (fail open)
  const body = (await res.json()) as { preferences?: UserPreferences };
  return body.preferences ?? {};
}

async function patchUserPreferences(
  body: UserPreferencesPatchBody
): Promise<UserPreferences> {
  const res = await fetch("/api/user/preferences", {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as {
    preferences?: UserPreferences;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof json.error === "string" ? json.error : "Failed to save preferences"
    );
  }
  return json.preferences ?? {};
}

/**
 * Apply a patch to a cached preferences object the same way the server's
 * `update_user_prefs` merge does, for an optimistic cache update. Returns a new
 * object; never mutates the input. The server response later reconciles it.
 */
export function applyPatchToPreferences(
  prefs: UserPreferences,
  body: UserPreferencesPatchBody
): UserPreferences {
  const next: UserPreferences = { ...prefs };

  if ("slackTaskReminderChannelId" in body) {
    const raw = body.slackTaskReminderChannelId;
    next.slackTaskReminderChannelId =
      typeof raw === "string" && raw.trim() !== "" ? raw.trim() : undefined;
  }
  if (body.markTourSeen || body.unmarkTourSeen) {
    const seen = new Set(prefs.seenTours ?? []);
    if (body.markTourSeen) seen.add(body.markTourSeen);
    if (body.unmarkTourSeen) seen.delete(body.unmarkTourSeen);
    next.seenTours = [...seen];
  }
  if (body.setSavedViewHidden) {
    next.savedViewHiddenByEntity = {
      ...(prefs.savedViewHiddenByEntity ?? {}),
      [body.setSavedViewHidden.entityName]: body.setSavedViewHidden.viewIds,
    };
  }
  if (body.setSavedViewOrder) {
    next.savedViewOrderByEntity = {
      ...(prefs.savedViewOrderByEntity ?? {}),
      [body.setSavedViewOrder.entityName]: body.setSavedViewOrder.viewIds,
    };
  }
  if (body.setTableColumnWidths) {
    next.tableColumnWidthsByKey = {
      ...(prefs.tableColumnWidthsByKey ?? {}),
      [body.setTableColumnWidths.tableKey]: body.setTableColumnWidths.widths,
    };
  }
  if (body.setTableColumnVisibility) {
    next.tableColumnVisibilityByKey = {
      ...(prefs.tableColumnVisibilityByKey ?? {}),
      [body.setTableColumnVisibility.tableKey]:
        body.setTableColumnVisibility.visibility,
    };
  }
  if (body.setTableColumnOrder) {
    next.tableColumnOrderByKey = {
      ...(prefs.tableColumnOrderByKey ?? {}),
      [body.setTableColumnOrder.tableKey]: body.setTableColumnOrder.order,
    };
  }
  if (body.setTablePageSize) {
    next.tablePageSizeByKey = {
      ...(prefs.tablePageSizeByKey ?? {}),
      [body.setTablePageSize.tableKey]: body.setTablePageSize.pageSize,
    };
  }
  if ("crmInboxLayout" in body) {
    next.crmInboxLayout = body.crmInboxLayout ?? undefined;
  }
  if ("crmInboxSelectedAccountIds" in body) {
    const ids = body.crmInboxSelectedAccountIds;
    // Mirrors the server: an empty array stores as absent, meaning "all accounts".
    next.crmInboxSelectedAccountIds =
      ids && ids.length > 0 ? [...new Set(ids)] : undefined;
  }
  if ("setMeetingNotesFollowup" in body) {
    next.meetingNotesFollowup = body.setMeetingNotesFollowup ?? undefined;
  }
  return next;
}

export interface UseUserPreferencesResult {
  /** The user's preferences ({} until loaded / when none exist). */
  preferences: UserPreferences;
  /** True while the first fetch is in flight. */
  isLoading: boolean;
  /** True once the preferences have been fetched at least once (cached). */
  isHydrated: boolean;
}

/**
 * Read the shared, app-wide preferences cache. Cheap to call from anywhere —
 * every caller shares one query (one network fetch, cached across navigation).
 */
export function useUserPreferences(): UseUserPreferencesResult {
  const { data, isLoading, isFetched } = useQuery({
    queryKey: USER_PREFERENCES_QUERY_KEY,
    queryFn: fetchUserPreferences,
    // Preferences change rarely and only via this app; hold them long and never
    // refetch just because a new page mounted a consumer (global default is
    // refetchOnMount:false — this reinforces it).
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  return {
    preferences: data ?? {},
    isLoading,
    isHydrated: isFetched,
  };
}

/**
 * Returns a STABLE `patch(body)` function that persists one preference change.
 * It optimistically updates the shared cache (so every reader reflects it
 * immediately), PATCHes the server, then reconciles the cache with the
 * authoritative full preferences the server returns. Fire-and-forget on the
 * caller's side; failures roll the optimistic change back and are surfaced via
 * the mutation's own error handling (best-effort — a failed pref write is never
 * fatal).
 */
export function useUpdateUserPreferences(): (
  body: UserPreferencesPatchBody
) => void {
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: patchUserPreferences,
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: USER_PREFERENCES_QUERY_KEY });
      const previous = queryClient.getQueryData<UserPreferences>(
        USER_PREFERENCES_QUERY_KEY
      );
      queryClient.setQueryData<UserPreferences>(
        USER_PREFERENCES_QUERY_KEY,
        (old) => applyPatchToPreferences(old ?? {}, body)
      );
      return { previous };
    },
    onError: (error, _body, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          USER_PREFERENCES_QUERY_KEY,
          context.previous
        );
      }
      console.warn("Failed to persist user preference:", error);
    },
    onSuccess: (serverPrefs) => {
      // Reconcile with the authoritative merged preferences from the server.
      queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, serverPrefs);
    },
  });
  // `mutate` is referentially stable across renders (TanStack Query guarantee),
  // so callers can safely depend on the returned function.
  return mutate;
}

/**
 * Like {@link useUpdateUserPreferences} but returns an AWAITABLE `patch(body)`
 * that resolves to the server's authoritative preferences (or throws on
 * failure). Use in UI that must show its own success/error feedback (e.g. the
 * settings Save button) — the cache is still updated optimistically and
 * reconciled with the server response, so all other readers stay in sync.
 */
export function useUpdateUserPreferencesAsync(): (
  body: UserPreferencesPatchBody
) => Promise<UserPreferences> {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: patchUserPreferences,
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: USER_PREFERENCES_QUERY_KEY });
      const previous = queryClient.getQueryData<UserPreferences>(
        USER_PREFERENCES_QUERY_KEY
      );
      queryClient.setQueryData<UserPreferences>(
        USER_PREFERENCES_QUERY_KEY,
        (old) => applyPatchToPreferences(old ?? {}, body)
      );
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, context.previous);
      }
    },
    onSuccess: (serverPrefs) => {
      queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, serverPrefs);
    },
  });
  return mutateAsync;
}
